// What every page needs before it does anything of its own: somewhere to put toasts, the account
// menu wired up, and the session resolved.
//
// Each page used to do this for itself, which is why the same logout handler and the same
// "displayName || fullName || email" existed in three places.

import type { ComponentType } from "preact";
import * as auth from "../legacy/auth.js";
import * as toast from "../legacy/toast.js";
import { mountIslands } from "../islands/mount.tsx";
import { ProgressHud } from "../components/chrome/ProgressHud.tsx";
import { AnalysisBadge } from "../components/chrome/AnalysisBadge.tsx";

export interface PageShellOptions {
    /** Called with the session state once it is known, and again whenever it changes. */
    onAuthStateChanged?: (isLoggedIn: boolean) => void;
    onConfigLoaded?: (config: unknown) => void;
    /** The components this page declares, on top of the ones every page has. */
    islands?: Record<string, ComponentType<Record<string, never>>>;
}

/**
 * The two things the header opens: the account menu, and — on a phone, where they do not fit in the
 * bar — the sections of the site. Only one of them is ever open, and both close on Escape, on a
 * click outside, and on a click inside, because every item in them either navigates or acts.
 */
function wireHeaderMenus(): void {
    const account = document.getElementById("userDropdownWrapper");
    const accountTrigger = document.getElementById("userMenuTrigger");
    const accountMenu = document.getElementById("userMiniDropdown");
    const header = document.getElementById("siteHeader");
    const sectionsTrigger = document.getElementById("siteMenuToggle");
    const sections = document.getElementById("siteNav");

    const closeGroups = wireNavGroups(sections);
    const closeAccount = () => {
        account?.classList.remove("open");
        accountTrigger?.setAttribute("aria-expanded", "false");
    };
    const closeSections = () => {
        closeGroups();
        header?.removeAttribute("data-menu");
        sectionsTrigger?.setAttribute("aria-expanded", "false");
    };
    const closeAll = () => {
        closeAccount();
        closeSections();
    };

    accountTrigger?.addEventListener("click", (event) => {
        event.stopPropagation();
        closeSections();
        const open = account?.classList.toggle("open");
        accountTrigger.setAttribute("aria-expanded", String(!!open));
    });
    sectionsTrigger?.addEventListener("click", (event) => {
        event.stopPropagation();
        closeAccount();
        const open = header?.getAttribute("data-menu") !== "open";
        if (open) {
            header?.setAttribute("data-menu", "open");
        } else {
            header?.removeAttribute("data-menu");
        }
        sectionsTrigger.setAttribute("aria-expanded", String(open));
    });

    document.addEventListener("click", (event) => {
        const target = event.target as Node;
        if (!account?.contains(target)) closeAccount();
        if (!sections?.contains(target) && !sectionsTrigger?.contains(target)) closeSections();
    });
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") closeAll();
    });
    accountMenu?.addEventListener("click", closeAccount);
    sections?.addEventListener("click", (event) => {
        // Unfolding a group is not leaving the panel; following one of its links is
        if ((event.target as Element).closest(".nav-group__trigger")) return;
        closeSections();
    });
}

/**
 * The groups of an account's bar (fragments/header.html, `.nav-group`): one open at a time, on a click, and on a
 * wide screen also while the pointer rests on it. Answers what closes them all.
 */
function wireNavGroups(nav: HTMLElement | null): () => void {
    const groups = Array.from(nav?.querySelectorAll<HTMLElement>(".nav-group") ?? []);
    const hover = window.matchMedia(HOVER_OPENS);
    const setOpen = (group: HTMLElement, open: boolean) => {
        group.classList.toggle("open", open);
        group.querySelector(".nav-group__trigger")?.setAttribute("aria-expanded", String(open));
    };
    const closeAll = () => groups.forEach((group) => setOpen(group, false));
    for (const group of groups) {
        group.querySelector(".nav-group__trigger")?.addEventListener("click", (event) => {
            event.stopPropagation();
            const open = !group.classList.contains("open");
            closeAll();
            setOpen(group, open);
        });
        group.addEventListener("mouseenter", () => {
            if (!hover.matches) return;
            closeAll();
            setOpen(group, true);
        });
        group.addEventListener("mouseleave", () => {
            if (hover.matches) setOpen(group, false);
        });
    }
    return closeAll;
}

/** Where a pointer can rest: there a group of the bar opens on hover, and everywhere on a click. */
const HOVER_OPENS = "(hover: hover) and (min-width: 48em)";

/**
 * Everything that has to be in place before a page does its own work: somewhere to put toasts, the
 * account menu wired, and the components mounted.
 *
 * Separate from resolving the session because the board page restores its saved state in between:
 * the server's configuration has to win over this device's, so it must be applied afterwards.
 */
export function setupPage(options: PageShellOptions = {}): void {
    toast.initToast({ toastContainer: document.getElementById("toastContainer") });
    wireHeaderMenus();
    // The header is the same everywhere, so what lives in it is mounted here rather than by each page.
    mountIslands({ "progress-hud": ProgressHud, "analysis-status": AnalysisBadge, ...(options.islands ?? {}) });
    auth.initAuth({
        navUserName: document.getElementById("navUserName"),
        logoutBtn: document.getElementById("logoutBtn"),
        googleLoginBtn: document.getElementById("googleLoginBtn"),
        userDropdownWrapper: document.getElementById("userDropdownWrapper"),
    }, {
        onAuthStateChanged: options.onAuthStateChanged,
        onConfigLoaded: options.onConfigLoaded,
    });
}

/** Asks the server who is logged in, which is what fills the header and the page. */
export function resolveSession(): Promise<void> {
    return auth.refreshAuthState();
}

export async function bootPage(options: PageShellOptions = {}): Promise<void> {
    setupPage(options);
    await resolveSession();
}
